/**
 * Performance API Cleanup Utility
 *
 * Prevents memory leaks from accumulated PerformanceEventTiming entries
 * Chrome DevTools showed 698 PerformanceEventTiming objects (+94.9 KB) leak
 *
 * This utility clears performance entries periodically to prevent unbounded growth
 */

import logger from './logger';

class PerformanceCleanup {
  constructor() {
    this.cleanupInterval = null;
    this.isRunning = false;
    this.observers = [];
    this.startCount = 0;
  }

  /**
   * Start periodic cleanup of performance entries
   * @param {number} intervalMs - Cleanup interval in milliseconds (default: 30000)
   */
  start(intervalMs = 30000) {
    this.startCount++;

    if (this.isRunning && this.cleanupInterval) {
      logger.warn(`[PerformanceCleanup] Already running (start call #${this.startCount}), skipping`);
      return;
    }

    if (this.cleanupInterval) {
      logger.warn(`[PerformanceCleanup] Stale interval detected (start call #${this.startCount}), clearing first`);
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    this.isRunning = true;

    // Initial cleanup
    this.cleanup();

    // Set up periodic cleanup
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, intervalMs);

    logger.log(`[PerformanceCleanup] Started #${this.startCount} (cleanup every ${intervalMs}ms)`);
  }

  /**
   * Stop periodic cleanup
   */
  stop() {
    if (!this.isRunning && !this.cleanupInterval) {
      logger.warn('[PerformanceCleanup] Not running, nothing to stop');
      return;
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    // Disconnect all PerformanceObservers to prevent memory leaks
    this.observers.forEach(observer => {
      try {
        observer.disconnect();
      } catch (e) {
        console.error('[PerformanceCleanup] Error disconnecting observer:', e);
      }
    });
    this.observers = [];

    this.isRunning = false;
    logger.log(`[PerformanceCleanup] Stopped (had ${this.startCount} start calls)`);
  }

  /**
   * Perform cleanup of performance entries
   */
  cleanup() {
    try {
      // Get counts before cleanup (for logging)
      const beforeCounts = {
        resources: performance.getEntriesByType('resource').length,
        marks: performance.getEntriesByType('mark').length,
        measures: performance.getEntriesByType('measure').length,
        navigation: performance.getEntriesByType('navigation').length,
        paint: performance.getEntriesByType('paint').length
      };

      // Clear performance entries
      performance.clearResourceTimings();
      performance.clearMarks();
      performance.clearMeasures();

      // Limit performance buffer size to prevent PerformanceEventTiming accumulation
      if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes) {
        try {
          performance.setResourceTimingBufferSize(50);
        } catch (e) {
          // Silently fail if not supported
        }
      }

      // Get counts after cleanup
      const afterCounts = {
        resources: performance.getEntriesByType('resource').length,
        marks: performance.getEntriesByType('mark').length,
        measures: performance.getEntriesByType('measure').length
      };

      if (process.env.NODE_ENV === 'development') {
        const totalCleared =
          (beforeCounts.resources - afterCounts.resources) +
          (beforeCounts.marks - afterCounts.marks) +
          (beforeCounts.measures - afterCounts.measures);

        if (totalCleared > 0) {
          logger.log(`[PerformanceCleanup] Cleared ${totalCleared} performance entries:`, {
            resources: beforeCounts.resources - afterCounts.resources,
            marks: beforeCounts.marks - afterCounts.marks,
            measures: beforeCounts.measures - afterCounts.measures
          });
        }
      }
    } catch (error) {
      console.error('[PerformanceCleanup] Error during cleanup:', error);
    }
  }

  /**
   * Get current performance entry counts
   * @returns {Object} Entry counts by type
   */
  getEntryCounts() {
    try {
      return {
        resources: performance.getEntriesByType('resource').length,
        marks: performance.getEntriesByType('mark').length,
        measures: performance.getEntriesByType('measure').length,
        navigation: performance.getEntriesByType('navigation').length,
        paint: performance.getEntriesByType('paint').length,
        total: performance.getEntries().length
      };
    } catch (error) {
      console.error('[PerformanceCleanup] Error getting entry counts:', error);
      return null;
    }
  }
}

// Create singleton instance
const performanceCleanup = new PerformanceCleanup();

export default performanceCleanup;
